// verify.ts — session verification + transcript editing (EGO-132 Phase C).
// A contractor opens a synced capture, checks their narration against the task
// structure, and corrects it. TWO views over the SAME edit overlay:
//   • Timeline — a waveform DAW editor (timeline.ts): drag step dividers, task
//     caps, and chunk edges/body against the real audio.
//   • List — the transcript grouped by sub-task with inline text + stepper edits.
// Edits NEVER mutate the raw transcript.json / task_events.jsonl — they layer
// into a transcript_edits.json overlay (core/src/edits.ts) applied on read and
// saved via the owner-scoped worker (POST /session/edits).
import { parseTaskEvents, wavToMono, type TaskWindow, type TxSegment } from "../../core/src/transcript.ts";
import {
  applyTranscriptEdits, emptyEdits, hasEdits,
  type TranscriptEdits, type MergedSegment,
} from "../../core/src/edits.ts";
import { mountTimeline, type TimelineCtrl } from "./timeline.ts";
import { transcribeBuffer } from "./transcribe.ts";
import { WORKER_URL } from "./apk.ts";
import { supabase } from "./auth.ts";

interface Transcript { audio_start_elapsed_ns?: number; segments: TxSegment[] }
interface TaskDef { id?: string; name?: string; subtasks?: string[] }

const esc = (s: unknown) => String(s).replace(/[&<>"']/g, (c) =>
  ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
const stHue = (i: number) => Math.round((i * 137.508) % 360);
const fmtT = (s: number) => `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, "0")}`;
const fmtTs = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`;
const STEP_NS = 100_000_000;   // 0.1s stepper granularity (list mode)

async function authHeader(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  return session ? { Authorization: `Bearer ${session.access_token}` } : {};
}
async function fetchFile(sid: string, path: string): Promise<Response> {
  return fetch(`${WORKER_URL}/session/file?session_id=${encodeURIComponent(sid)}&path=${encodeURIComponent(path)}`,
    { headers: await authHeader() });
}
async function fetchJson<T>(sid: string, path: string): Promise<T | null> {
  try { const r = await fetchFile(sid, path); return r.ok ? await r.json() as T : null; } catch { return null; }
}
async function fetchText(sid: string, path: string): Promise<string | null> {
  try { const r = await fetchFile(sid, path); return r.ok ? await r.text() : null; } catch { return null; }
}

// ── module state for the open session ────────────────────────────────────────
let sid = "";
let rawSegs: TxSegment[] = [];
let rawWindows: TaskWindow[] = [];
let task: TaskDef | null = null;
let anchor = 0;
let edits: TranscriptEdits = emptyEdits(0);
let savedSnapshot = "";
let addCounter = 0;
let editKey: string | null = null;         // selected/expanded chunk (shared by both modes)
let mode: "timeline" | "list" = "timeline";
let audioUrl: string | null = null;
let audioEl: HTMLAudioElement | null = null;
let audioBuf: ArrayBuffer | null = null;      // kept for Re-transcribe (no re-download)
let metaAudioStartNs: number | null = null;   // meta.audio.start_elapsed_ns — the true anchor
let waveform: Float32Array | null = null;
let audioDur = 0;                           // seconds (0 until decoded)
let audioReady = false;
let tl: { update: () => void; destroy: () => void } | null = null;
let onCloseCb: (() => void) | null = null;
let reviewedAt: string | null = null;   // sessions.reviewed_at — explicit sign-off

const nowMs = () => Date.now();
const secToNs = (sec: number) => Math.round(anchor + sec * 1e9);
const merged = () => applyTranscriptEdits(rawSegs, rawWindows, edits, anchor);
const keyFor = (m: MergedSegment) => m.added ? `add:${m.addedId}` : (m.origKey ?? "");
const findByKey = (key: string) => merged().segments.find((m) => keyFor(m) === key);

export function isVerifyOpen(): boolean { return !document.getElementById("verify")!.hidden; }

// Space = play/pause transport while the review view is open, unless you're
// typing. preventDefault stops both page-scroll and activating a focused
// button, so Space is always the transport here.
function onVerifyKey(e: KeyboardEvent) {
  if (e.code !== "Space" || e.metaKey || e.ctrlKey || e.altKey) return;
  const t = e.target as HTMLElement | null;
  if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
  if (!audioEl || !audioEl.src) return;
  e.preventDefault();
  audioEl.paused ? void audioEl.play() : audioEl.pause();
}

export function closeVerify(): void {
  if (dirty() && !confirm("You have unsaved transcript edits. Close without saving?")) return;
  document.removeEventListener("keydown", onVerifyKey);
  tl?.destroy(); tl = null;
  const root = document.getElementById("verify")!;
  root.hidden = true;
  root.innerHTML = "";
  if (audioUrl) { URL.revokeObjectURL(audioUrl); audioUrl = null; }
  audioEl = null; waveform = null; audioReady = false; audioDur = 0;
  audioBuf = null; metaAudioStartNs = null;
  onCloseCb?.(); onCloseCb = null;
}

function dirty(): boolean { return JSON.stringify(edits) !== savedSnapshot; }

export async function openVerify(id: string, opts: { onClose?: () => void } = {}): Promise<void> {
  sid = id;
  onCloseCb = opts.onClose ?? null;
  editKey = null;
  document.removeEventListener("keydown", onVerifyKey);   // no double-bind on reopen
  document.addEventListener("keydown", onVerifyKey);
  const root = document.getElementById("verify")!;
  root.hidden = false;
  root.innerHTML = `<div class="vf-loading">Loading ${esc(id)}…</div>`;

  const [tx, taskDef, eventsText, savedEdits, meta, ledger] = await Promise.all([
    fetchJson<Transcript>(id, "transcript.json"),
    fetchJson<TaskDef>(id, "task.json"),
    fetchText(id, "task_events.jsonl"),
    fetchJson<TranscriptEdits>(id, "transcript_edits.json"),
    fetchJson<any>(id, "meta.json"),
    supabase.from("sessions").select("reviewed_at").eq("id", id).maybeSingle(),
  ]);
  reviewedAt = (ledger?.data as any)?.reviewed_at ?? null;
  task = taskDef;
  // The audio's true sample-0 clock (nested, latency-corrected) — used when we
  // re-transcribe so the new transcript is anchored correctly.
  metaAudioStartNs = typeof meta?.audio?.start_elapsed_ns === "number" ? meta.audio.start_elapsed_ns : null;
  rawSegs = (tx?.segments ?? []).filter((s) => s.text?.trim());
  rawWindows = eventsText ? parseTaskEvents(eventsText).segs : [];
  anchor = tx?.audio_start_elapsed_ns ?? (rawWindows[0]?.t_start_ns ?? 0);
  edits = savedEdits && savedEdits.schema === 1
    ? { ...emptyEdits(nowMs()), ...savedEdits, segments: savedEdits.segments ?? {}, added: savedEdits.added ?? [], windows: savedEdits.windows ?? {} }
    : emptyEdits(nowMs());
  savedSnapshot = JSON.stringify(edits);
  addCounter = edits.added?.length ?? 0;
  // Free capture (no sub-tasks) → the timeline still works, but the list is the
  // simpler default there; a task-capture session defaults to the timeline.
  mode = rawWindows.length ? "timeline" : "list";

  renderShell(root);
  loadAudio();       // decodes waveform + duration, then (re)renders the body
  renderBody();
}

function stepLabel(st: MergedSegment["subtask"]): string {
  if (st === "intro") return "Intro";
  if (st === "outro") return "Outro";
  if (typeof st === "number") return `Step ${st + 1}${task?.subtasks?.[st] ? " · " + task.subtasks[st] : ""}`;
  return "Untagged";
}

// ── shell: header (mode toggle, save/discard) + #vfBody ──────────────────────
function renderShell(root: HTMLElement) {
  root.innerHTML = `
    <div class="vf-head">
      <button id="vfBack" class="secondary">← Back</button>
      <div class="vf-titles">
        <div class="vf-title">${esc(task?.name || "Session")}</div>
        <div class="vf-sub">${esc(sid)}${task ? "" : " · free capture"}</div>
      </div>
      <div class="vf-modes">
        <button data-mode="timeline" class="${mode === "timeline" ? "on" : ""}">Timeline</button>
        <button data-mode="list" class="${mode === "list" ? "on" : ""}">List</button>
      </div>
      <span id="vfDirty" class="vf-dirty" hidden>● unsaved</span>
      <button id="vfRetx" class="secondary" title="Re-run speech-to-text over this session's audio">↻ Re-transcribe</button>
      <button id="vfDiscard" class="secondary" disabled>Discard</button>
      <button id="vfSave" disabled>Save corrections</button>
      <button id="vfReviewed" class="secondary"></button>
    </div>
    <div id="vfBody"></div>`;
  root.querySelector<HTMLButtonElement>("#vfBack")!.onclick = closeVerify;
  root.querySelector<HTMLButtonElement>("#vfSave")!.onclick = save;
  root.querySelector<HTMLButtonElement>("#vfDiscard")!.onclick = discard;
  root.querySelector<HTMLButtonElement>("#vfRetx")!.onclick = reTranscribe;
  root.querySelector<HTMLButtonElement>("#vfReviewed")!.onclick = toggleReviewed;
  paintReviewed();
  for (const b of root.querySelectorAll<HTMLButtonElement>("[data-mode]")) {
    b.onclick = () => { if (mode === b.dataset.mode) return; mode = b.dataset.mode as any;
      for (const x of root.querySelectorAll<HTMLButtonElement>("[data-mode]")) x.classList.toggle("on", x.dataset.mode === mode);
      renderBody(); };
  }
}

function markDirty() {
  const el = document.getElementById("verify")!;
  const d = dirty();
  (el.querySelector("#vfSave") as HTMLButtonElement).disabled = !d;
  (el.querySelector("#vfDiscard") as HTMLButtonElement).disabled = !d;
  el.querySelector("#vfDirty")!.toggleAttribute("hidden", !d);
}

function renderBody() {
  tl?.destroy(); tl = null;
  const body = document.getElementById("vfBody")!;
  if (mode === "timeline") {
    if (!audioReady) { body.innerHTML = `<div class="vf-loading">Decoding audio for the timeline…</div>`; return; }
    tl = mountTimeline(body, buildCtrl());
  } else {
    renderList(body);
  }
  markDirty();
}

// ── shared overlay mutators (both modes) ─────────────────────────────────────
function editSeg(key: string, mut: { text?: string; edge?: "s" | "e"; ns?: number; toggleDelete?: boolean; remove?: boolean }) {
  if (key.startsWith("add:")) {
    const id = key.slice(4);
    if (mut.remove) { edits.added = (edits.added ?? []).filter((a) => a.id !== id); return; }
    const a = (edits.added ?? []).find((x) => x.id === id);
    if (!a) return;
    if (mut.text != null) a.text = mut.text;
    if (mut.edge === "s" && mut.ns != null) a.t_start_ns = mut.ns;
    if (mut.edge === "e" && mut.ns != null) a.t_end_ns = mut.ns;
  } else {
    const e = (edits.segments![key] ??= {});
    if (mut.text != null) e.text = mut.text;
    if (mut.edge === "s" && mut.ns != null) e.t_start_ns = mut.ns;
    if (mut.edge === "e" && mut.ns != null) e.t_end_ns = mut.ns;
    if (mut.toggleDelete) e.deleted = !e.deleted;
  }
  markDirty();
}
function delKey(key: string) {
  if (key.startsWith("add:")) editSeg(key, { remove: true });
  else editSeg(key, { toggleDelete: true });
}
function moveBody(key: string, startNs: number) {
  const cur = findByKey(key); if (!cur) return;
  const len = cur.t_end_ns - cur.t_start_ns;
  editSeg(key, { edge: "s", ns: startNs });
  editSeg(key, { edge: "e", ns: startNs + len });
}
// A divider is the SHARED boundary between step i and i+1 — write both edges.
function moveDivider(i: number, ns: number) {
  (edits.windows ??= {});
  (edits.windows[String(i)] ??= {}).t_end_ns = ns;
  (edits.windows[String(i + 1)] ??= {}).t_start_ns = ns;
  markDirty();
}
function moveCap(which: "start" | "end", ns: number) {
  (edits.windows ??= {});
  const last = rawWindows.length - 1;
  if (which === "start") (edits.windows["0"] ??= {}).t_start_ns = ns;
  else (edits.windows[String(last)] ??= {}).t_end_ns = ns;
  markDirty();
}
function addChunkAt(sec: number) {
  const startNs = secToNs(sec);
  const w = merged().windows.find((win) => sec >= (win.t_start_ns - anchor) / 1e9 && sec < (win.t_end_ns - anchor) / 1e9);
  const id = `add-${++addCounter}`;
  edits.added = edits.added ?? [];
  edits.added.push({ id, t_start_ns: startNs, t_end_ns: startNs + 2e9, text: "", subtask: w ? w.index : undefined, kind: "narration" });
  editKey = `add:${id}`;
  markDirty();
}

// ── timeline controller ──────────────────────────────────────────────────────
function buildCtrl(): TimelineCtrl {
  return {
    read: () => { const m = merged(); return { segments: m.segments, windows: m.windows, duration: timelineDuration(), waveDuration: audioDur, anchor }; },
    waveform,
    audioEl: () => audioEl,
    keyFor,
    selectedKey: () => editKey,
    snap: () => true,
    onSelect: (k) => { editKey = k; },
    editEdge: (k, edge, ns) => editSeg(k, { edge, ns }),
    moveBody,
    moveDivider,
    moveCap,
    addAt: (sec) => addChunkAt(sec),
    editText: (k, text) => editSeg(k, { text }),
    del: (k) => delKey(k),
  };
}
function timelineDuration(): number {
  const segEnd = rawSegs.length ? Math.max(...merged().segments.map((s) => s.end)) : 0;
  const winEnd = rawWindows.length ? (merged().windows[merged().windows.length - 1].t_end_ns - anchor) / 1e9 : 0;
  return Math.max(audioDur, segEnd, winEnd, 1);
}

// ── list mode ────────────────────────────────────────────────────────────────
function renderList(body: HTMLElement) {
  body.innerHTML = `
    <div class="vf-player">
      <button id="vfPlay" class="vf-playbtn" ${audioReady ? "" : "disabled"}>▶</button>
      <span id="vfClock" class="vf-clock">0:00</span>
      <div id="vfTrack" class="vf-track"><div id="vfPlayhead" class="vf-playhead"></div></div>
      <span id="vfDur" class="vf-dur">0:00</span>
    </div>
    <div class="vf-now" id="vfNow"></div>
    <div class="vf-toolbar">
      <button id="vfAdd" class="secondary" title="Add a transcript chunk at the playhead">＋ Add chunk at playhead</button>
      <button id="vfBounds" class="secondary" title="Adjust sub-task boundaries">⇥ Step boundaries</button>
    </div>
    <div id="vfBoundsPanel" class="vf-bounds" hidden></div>
    <div id="vfList" class="vf-list"></div>`;
  const $ = (i: string) => body.querySelector<HTMLElement>("#" + i)!;
  $("vfAdd").onclick = () => { addChunkAt(audioEl?.currentTime ?? 0); rebuildList(); setTimeout(() => document.querySelector<HTMLTextAreaElement>(".vf-row.editing .vf-ta")?.focus(), 0); };
  $("vfBounds").onclick = () => { const p = $("vfBoundsPanel"); p.hidden = !p.hidden; $("vfBounds").classList.toggle("on", !p.hidden); if (!p.hidden) renderBounds(); };
  wireListPlayer();
  rebuildList();
}

function wireListPlayer() {
  const play = document.getElementById("vfPlay") as HTMLButtonElement | null;
  if (!play || !audioEl) return;
  play.onclick = () => { audioEl!.paused ? void audioEl!.play() : audioEl!.pause(); };
  audioEl.onplay = () => { play.textContent = "⏸"; };
  audioEl.onpause = () => { play.textContent = "▶"; };
  const total = timelineDuration();
  document.getElementById("vfDur")!.textContent = fmtT(total);
  audioEl.ontimeupdate = () => {
    if (mode !== "list") return;
    const t = audioEl!.currentTime;
    document.getElementById("vfClock")!.textContent = fmtTs(t);
    const ph = document.getElementById("vfPlayhead")!;
    ph.style.left = `${Math.min(100, (t / total) * 100)}%`;
    const w = rawWindows.find((win) => { const a = (win.t_start_ns - anchor) / 1e9, b = (win.t_end_ns - anchor) / 1e9; return t >= a && t < b; });
    const now = document.getElementById("vfNow")!;
    now.textContent = w ? `Now: ${stepLabel(w.index)}` : (rawWindows.length ? "Now: between steps" : "");
    now.style.setProperty("--h", String(w ? stHue(w.index) : 210));
  };
}

function rebuildList() {
  if (mode !== "list") return;
  const listEl = document.getElementById("vfList");
  if (!listEl) return;
  // chapter strip in the player
  const track = document.getElementById("vfTrack")!;
  const ph = document.getElementById("vfPlayhead")!;
  track.querySelectorAll(".vf-seg").forEach((e) => e.remove());
  const total = timelineDuration();
  const m = merged();
  for (const w of m.windows) {
    const a = (w.t_start_ns - anchor) / 1e9, b = (w.t_end_ns - anchor) / 1e9;
    const seg = document.createElement("div"); seg.className = "vf-seg";
    seg.style.cssText = `left:${(a / total) * 100}%;width:${((b - a) / total) * 100}%;--h:${stHue(w.index)}`;
    seg.title = w.text; seg.innerHTML = `<span>${w.index + 1}</span>`;
    seg.onclick = () => { if (audioEl) { audioEl.currentTime = a; void audioEl.play(); } };
    track.insertBefore(seg, ph);
  }
  // grouped rows
  listEl.innerHTML = "";
  if (!m.segments.length) { listEl.innerHTML = `<div class="vf-empty">No transcript yet — synced before ingest transcription, or no narration.</div>`; return; }
  let lastGroup: string | null = null;
  for (const s of m.segments) {
    const g = stepLabel(s.subtask);
    if (g !== lastGroup) {
      lastGroup = g;
      const gh = document.createElement("div"); gh.className = "vf-group";
      gh.style.setProperty("--h", String(typeof s.subtask === "number" ? stHue(s.subtask) : 210));
      gh.textContent = g; listEl.appendChild(gh);
    }
    listEl.appendChild(listRow(s));
  }
  if (!document.getElementById("vfBoundsPanel")!.hidden) renderBounds();
}

function listRow(s: MergedSegment): HTMLElement {
  const row = document.createElement("div");
  const editing = keyFor(s) === editKey;
  row.className = "vf-row" + (s.kind === "wake" ? " wake" : "") + (s.deleted ? " deleted" : "") + (editing ? " editing" : "");
  const badges = (s.added ? `<span class="vf-badge add">added</span>` : "") + (s.edited ? `<span class="vf-badge edit">edited</span>` : "");
  if (editing) {
    row.innerHTML = `
      <div class="vf-edit">
        <textarea class="vf-ta" rows="2">${esc(s.text)}</textarea>
        <div class="vf-times">
          <span class="vf-tlabel">start</span>${stepBtns("s")}<span class="vf-tv">${fmtTs(s.start)}</span>
          <button class="vf-mini" data-set="s" title="Set start to playhead">⇤ph</button>
          <span class="vf-tlabel">end</span>${stepBtns("e")}<span class="vf-tv">${fmtTs(s.end)}</span>
          <button class="vf-mini" data-set="e" title="Set end to playhead">ph⇥</button>
          <span class="vf-grow"></span>
          <button class="vf-mini danger" data-del>${s.added ? "Remove" : s.deleted ? "Restore" : "Delete"}</button>
          <button class="vf-mini" data-done>Done</button>
        </div>
      </div>`;
    wireListEdit(row, s);
  } else {
    row.innerHTML = `<span class="vf-t">${fmtTs(s.start)}</span>` +
      `<span class="vf-txt">${s.kind === "wake" ? "⟳ " : ""}${esc(s.text) || "<em>empty</em>"}${s.deleted ? " (deleted)" : ""}</span>` +
      `<span class="vf-badges">${badges}</span>`;
    row.onclick = () => { editKey = keyFor(s); rebuildList(); setTimeout(() => document.querySelector<HTMLTextAreaElement>(".vf-row.editing .vf-ta")?.focus(), 0); };
  }
  return row;
}
const stepBtns = (which: "s" | "e") =>
  `<span class="vf-step"><button data-step="${which}" data-dir="-1">−</button><button data-step="${which}" data-dir="1">+</button></span>`;

function wireListEdit(row: HTMLElement, s: MergedSegment) {
  const key = keyFor(s);
  const ta = row.querySelector<HTMLTextAreaElement>(".vf-ta")!;
  ta.oninput = () => editSeg(key, { text: ta.value });
  for (const b of row.querySelectorAll<HTMLButtonElement>("[data-step]")) {
    b.onclick = (e) => { e.stopPropagation(); const edge = b.dataset.step as "s" | "e"; const cur = findByKey(key)!;
      editSeg(key, { edge, ns: (edge === "s" ? cur.t_start_ns : cur.t_end_ns) + Number(b.dataset.dir) * STEP_NS });
      rebuildList(); setTimeout(() => document.querySelector<HTMLTextAreaElement>(".vf-row.editing .vf-ta")?.focus(), 0); };
  }
  for (const b of row.querySelectorAll<HTMLButtonElement>("[data-set]")) {
    b.onclick = (e) => { e.stopPropagation(); editSeg(key, { edge: b.dataset.set as "s" | "e", ns: secToNs(audioEl?.currentTime ?? 0) }); rebuildList(); };
  }
  row.querySelector<HTMLButtonElement>("[data-del]")!.onclick = (e) => { e.stopPropagation(); if (s.added) editKey = null; delKey(key); rebuildList(); };
  row.querySelector<HTMLButtonElement>("[data-done]")!.onclick = (e) => { e.stopPropagation(); editKey = null; rebuildList(); };
}

function renderBounds() {
  const panel = document.getElementById("vfBoundsPanel"); if (!panel) return;
  const m = merged();
  if (!m.windows.length) { panel.innerHTML = `<div class="vf-empty">Free capture — no sub-tasks to bound.</div>`; return; }
  const sec = (ns: number) => (ns - anchor) / 1e9;
  const brow = (label: string, s: number, kind: string, idx: number, edge: "s" | "e") => `
    <div class="vf-brow"><span class="vf-blabel">${esc(label)}</span>
      <span class="vf-step"><button data-b="${kind}" data-i="${idx}" data-e="${edge}" data-dir="-1">−</button><button data-b="${kind}" data-i="${idx}" data-e="${edge}" data-dir="1">+</button></span>
      <span class="vf-tv">${fmtTs(s)}</span>
      <button class="vf-mini" data-bset="${kind}" data-i="${idx}" data-e="${edge}">⇤ playhead</button></div>`;
  const first = m.windows[0], last = m.windows[m.windows.length - 1];
  panel.innerHTML =
    `<div class="vf-bhead">Task envelope</div>` +
    brow("Task start", sec(first.t_start_ns), "cap", 0, "s") +
    brow("Task end", sec(last.t_end_ns), "cap", last.index, "e") +
    `<div class="vf-bhead">Step boundaries</div>` +
    m.windows.slice(0, -1).map((w) => brow(`After step ${w.index + 1}`, sec(w.t_end_ns), "div", w.index, "e")).join("");
  for (const btn of panel.querySelectorAll<HTMLButtonElement>("[data-b]")) {
    btn.onclick = () => { const dir = Number(btn.dataset.dir), i = Number(btn.dataset.i);
      const curNs = boundNs(btn.dataset.b!, i, btn.dataset.e as "s" | "e");
      writeBound(btn.dataset.b!, i, btn.dataset.e as "s" | "e", curNs + dir * STEP_NS); rebuildList(); };
  }
  for (const btn of panel.querySelectorAll<HTMLButtonElement>("[data-bset]")) {
    btn.onclick = () => { writeBound(btn.dataset.bset!, Number(btn.dataset.i), btn.dataset.e as "s" | "e", secToNs(audioEl?.currentTime ?? 0)); rebuildList(); };
  }
}
function boundNs(kind: string, i: number, edge: "s" | "e"): number {
  const m = merged();
  if (kind === "cap") return edge === "s" ? m.windows[0].t_start_ns : m.windows[m.windows.length - 1].t_end_ns;
  return m.windows.find((w) => w.index === i)!.t_end_ns;   // "div" = boundary after step i
}
function writeBound(kind: string, i: number, edge: "s" | "e", ns: number) {
  if (kind === "cap") moveCap(edge === "s" ? "start" : "end", ns);
  else moveDivider(i, ns);   // boundary after step i = shared divider between i and i+1
}

// ── audio: decode for waveform + duration, keep a blob URL for playback ───────
function computeWaveform(samples: Float32Array, rate: number, dur: number) {
  const bins = Math.min(4000, Math.max(600, Math.floor(dur * 40)));
  const out = new Float32Array(bins);
  const per = samples.length / bins;
  let max = 1e-6;
  for (let i = 0; i < bins; i++) {
    let peak = 0; const a = Math.floor(i * per), b = Math.min(samples.length, Math.floor((i + 1) * per));
    for (let j = a; j < b; j++) { const v = Math.abs(samples[j]); if (v > peak) peak = v; }
    out[i] = peak; if (peak > max) max = peak;
  }
  for (let i = 0; i < bins; i++) out[i] /= max;
  return out;
}

function loadAudio() {
  const audio = new Audio();
  audioEl = audio;
  const finish = () => { audioReady = true; renderBody(); };
  fetchFile(sid, "audio.wav")
    .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
    .then((buf) => {
      audioBuf = buf;
      audioUrl = URL.createObjectURL(new Blob([buf], { type: "audio/wav" }));
      try {
        const { rate, samples } = wavToMono(buf);
        audioDur = samples.length / rate;                 // provisional (WAV-header derived)
        waveform = computeWaveform(samples, rate, audioDur);
      } catch { /* undecodable wav → no waveform; timeline still works off window extent */ }
      audio.src = audioUrl;
      // The <audio> element's own duration is authoritative for playback — the
      // waveform maps to it so it can't drift from the playhead. Wait for it
      // (with a timeout so we never hang), then render.
      let done = false;
      const go = () => { if (done) return; done = true;
        if (isFinite(audio.duration) && audio.duration > 0) audioDur = audio.duration;
        finish(); };
      audio.addEventListener("loadedmetadata", go, { once: true });
      audio.addEventListener("error", go, { once: true });
      setTimeout(go, 1500);
    })
    .catch(finish);   // no audio: still usable for text, just no playback/waveform
}

// ── save / discard ───────────────────────────────────────────────────────────
async function save() {
  const el = document.getElementById("verify")!;
  const saveBtn = el.querySelector<HTMLButtonElement>("#vfSave")!;
  saveBtn.disabled = true; saveBtn.textContent = "Saving…";
  try {
    await saveOverlay();
    saveBtn.textContent = "Saved ✓";
    setTimeout(() => { saveBtn.textContent = "Save corrections"; markDirty(); }, 1400);
  } catch (e: any) {
    saveBtn.textContent = "Save failed";
    alert(`Couldn't save corrections: ${e?.message ?? e}`);
    setTimeout(() => { saveBtn.textContent = "Save corrections"; markDirty(); }, 1400);
  }
}

/**
 * Re-run STT over this session's audio and replace derived/transcript.json.
 * Worth doing when the pipeline's guards improve (e.g. the out-of-range guard
 * that drops segments the model invents past the end of the audio).
 *
 * Per-chunk text/time edits are keyed by the ORIGINAL segment's t_start_ns, so
 * a fresh transcript orphans them — they're cleared. Added chunks and
 * step-boundary edits carry absolute times / indices and survive.
 */
async function reTranscribe() {
  const btn = document.querySelector<HTMLButtonElement>("#vfRetx");
  if (!btn || !audioBuf) { alert("Audio isn't loaded yet — try again in a moment."); return; }
  const nSegEdits = Object.keys(edits.segments ?? {}).length;
  const warn = nSegEdits
    ? `\n\n${nSegEdits} per-chunk text/time edit(s) will be discarded (they point at the old segments). Added chunks and step-boundary changes are kept.`
    : "";
  if (!confirm(`Re-run speech-to-text over this session's audio and replace the transcript?${warn}`)) return;

  const anchorForStt = metaAudioStartNs ?? anchor;
  const label = btn.textContent;
  btn.disabled = true;
  try {
    const fresh = await transcribeBuffer(audioBuf, {
      taskEventsText: rawWindows.length ? await fetchText(sid, "task_events.jsonl") : null,
      audioStartNs: anchorForStt,
      taskName: task?.name,
      onProgress: (i, n) => { btn.textContent = `Transcribing ${i + 1}/${n}…`; },
    });
    btn.textContent = "Saving…";
    const r = await fetch(`${WORKER_URL}/session/transcript`, {
      method: "POST", headers: { ...(await authHeader()), "content-type": "application/json" },
      body: JSON.stringify({ session_id: sid, transcript: fresh }),
    });
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);

    // Adopt the new transcript in place and drop the now-orphaned segment edits.
    rawSegs = fresh.segments.filter((s) => s.text?.trim());
    anchor = fresh.audio_start_elapsed_ns ?? anchorForStt;
    edits.segments = {};
    editKey = null;
    await saveOverlay();          // keep R2's overlay consistent with what we just cleared
    renderBody();
    const dropped = fresh.warnings?.length ?? 0;
    btn.textContent = "Re-transcribed ✓";
    setTimeout(() => { btn.textContent = label; btn.disabled = false; }, 1600);
    if (dropped) console.warn("[verify] transcript warnings:", fresh.warnings);
  } catch (e: any) {
    alert(`Re-transcribe failed: ${e?.message ?? e}`);
    btn.textContent = label;
    btn.disabled = false;
  }
}

/** POST the current overlay (shared by Save and Re-transcribe). */
async function saveOverlay() {
  edits.edited_at_ms = nowMs();
  const payload = hasEdits(edits) ? edits : emptyEdits(nowMs());
  const r = await fetch(`${WORKER_URL}/session/edits`, {
    method: "POST", headers: { ...(await authHeader()), "content-type": "application/json" },
    body: JSON.stringify({ session_id: sid, edits: payload }),
  });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  savedSnapshot = JSON.stringify(edits);
}

function discard() {
  if (!confirm("Discard all unsaved edits and revert to the last saved version?")) return;
  edits = JSON.parse(savedSnapshot);
  editKey = null;
  renderBody();
}

// ── Mark reviewed — the explicit sign-off (review doorway Phase 2) ───────────
// Deliberately separate from Save: reviewing and editing are different acts,
// and a session that needed no edits still gets signed off. The stamp lives on
// the ledger row (sessions.reviewed_at) via the owner-scoped worker route.
function paintReviewed() {
  const btn = document.querySelector<HTMLButtonElement>("#vfReviewed");
  if (!btn) return;
  btn.classList.toggle("done", !!reviewedAt);
  btn.textContent = reviewedAt ? "Reviewed ✓" : "✓ Mark reviewed";
  btn.title = reviewedAt
    ? `Marked reviewed ${new Date(reviewedAt).toLocaleString()} — click to un-mark.`
    : "Sign off that this session's transcript is correct.";
}

async function toggleReviewed() {
  const btn = document.querySelector<HTMLButtonElement>("#vfReviewed")!;
  const marking = !reviewedAt;
  if (!marking && !confirm("Un-mark this session as reviewed?")) return;
  if (marking && dirty()) {
    if (!confirm("You have unsaved corrections — save them and mark this session reviewed?")) return;
  }
  btn.disabled = true;
  try {
    if (marking && dirty()) await saveOverlay();
    const r = await fetch(`${WORKER_URL}/session/reviewed`, {
      method: "POST", headers: { ...(await authHeader()), "content-type": "application/json" },
      body: JSON.stringify({ session_id: sid, reviewed: marking }),
    });
    if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
    reviewedAt = (await r.json()).reviewed_at ?? null;
    markDirty();   // a pre-mark save cleared the dirty state — reflect it
  } catch (e: any) {
    alert(`Couldn't update the reviewed state: ${e?.message ?? e}`);
  }
  btn.disabled = false;
  paintReviewed();
}
